import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@mui/styles';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { Grid, Button, Typography } from '@mui/material';

import { Loader } from 'ui';
import { useSnackbar } from 'shared/hooks/useSnackbar';
import { addNotificationAction } from 'store/notifications/notificationsActions';
import { downloadAsyncService, DownloadStatus } from 'services/download/downloadAsyncService';
import clsx from 'clsx';

const useStyles = makeStyles(theme => ({
  item: {
    marginTop: theme.spacing(1),
    display: 'flex',
    justifyContent: 'flex-between',
    '&:last-child': {
      borderWidth: 0,
    },
  },
  name: {
    display: 'flex',
    alignItems: 'center',
  },
  button: {
    textTransform: 'none',
  },
  icon: {
    width: 20,
    height: 20,
    marginRight: 5,
  },
  active: {
    borderWidth: '0 0 1px 0',
    borderStyle: 'dashed',
    borderColor: theme.palette.text.primary,
  },
}));

const SummaryCardDocuments = ({ documentTypes, documentHandling, disabled, className }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [isPending, setIsPending] = useState(false);
  const [filesPending, setFilesPending] = useState([]);
  const { info } = useSnackbar();

  const throwFetchError = (message = null) =>
    dispatch(
      addNotificationAction({
        variant: 'error',
        message: message || t('Failed to get document. Please try again or contact.'),
      }),
    );

  const handleDownloadNotReady = (message = 'Document not ready') => info(message);

  const handleDownload = async ({ path, name, options, retries }) => {
    if (path === 'documentsModal' && documentHandling) {
      documentHandling.defaultDocument(name);
      documentHandling.toggleModal();
    } else {
      setIsPending(true);
      setFilesPending([...filesPending, path]);

      const result = await downloadAsyncService({
        path,
        name,
        options,
        retries,
        onError: throwFetchError,
        onDownloadNotReady: handleDownloadNotReady,
      });
      if (result !== DownloadStatus.PENDING) {
        setFilesPending(filesPending.filter(value => value !== path));
      }
      setIsPending(false);
    }
  };

  return <>
    {documentTypes.map(item => (
      <Grid
        container
        justifyContent="space-between"
        className={clsx(
          classes.item,
          {
            [classes.active]: !filesPending.includes(item.path),
          },
          className,
        )}
        key={item.title}
      >
        <Grid item className={classes.name}>
          <Typography variant="body2">{item.title}</Typography>
        </Grid>
        <Grid item>
          <Button
            onClick={() => handleDownload(item)}
            className={classes.button}
            disabled={filesPending.includes(item.path) || disabled}
          >
            <PictureAsPdfIcon className={classes.icon} />
            {t('View')}
            <ChevronRightIcon />
          </Button>
        </Grid>
      </Grid>
    ))}
    {isPending && <Loader center />}
  </>;
};

SummaryCardDocuments.defaultProps = {
  className: null,
  documentHandling: undefined,
  disabled: false,
};

SummaryCardDocuments.propTypes = {
  className: PropTypes.string,
  documentTypes: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      path: PropTypes.string.isRequired,
      options: PropTypes.object,
      retries: PropTypes.number,
    }),
  ).isRequired,
  documentHandling: PropTypes.object,
  disabled: PropTypes.bool,
};

export default SummaryCardDocuments;
